import { css, PrimaryButton } from "@sourceful/shared-components";
import { SanityTabsSection } from "@sourceful/shared-types/src/sanity/layouts/tabsSection";
import { useState, MouseEvent, useRef } from "react";
import { BasePageSection } from "..";
import { GridSection } from "../GridSection/GridSection";
import { SlidesSection } from "../SlidesSection/SlidesSection";

const tabsCss = css({
  display: "flex",
  justifyContent: "center",
  my: "$block_m1",
});

const tablistCss = css({
  margin: "0 auto",
  display: "inline-flex",
  flexWrap: "wrap",
  gap: "$inline_s1",
  justifyContent: "center",
  backgroundColor: "$palette3_colour1_tint70",
  //padding: "$inline_xs1 $inline_xs1",
  borderRadius: "30px",
});

const tabButtonCss = css({});

const tabPanelCss = css({
  variants: {
    hidden: {
      yes: {
        display: "none",
      },
      no: {},
    },
  },
});

interface ITabsSectionProps {
  section: SanityTabsSection;
}

const TabsSection = ({ section }: ITabsSectionProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [focusTabIndex, setFocusTabIndex] = useState(0);

  const tabsRef = useRef<HTMLDivElement>(null);

  const tabClick = (event: MouseEvent<HTMLButtonElement>) => {
    //console.log("TabsSection.tabClick", event);

    const buttonEl = event.target as HTMLButtonElement,
      index = parseInt(buttonEl.getAttribute("data-index") || "0");

    setActiveTabIndex(index);
    setFocusTabIndex(index);
  };

  const focusTabButtonByIndex = (index: number) => {
    //console.log("TabsSection.focusTabButtonByIndex", index);

    const buttonEl = tabsRef.current?.querySelector(
      `button:nth-child(${index + 1})`
    ) as HTMLButtonElement;

    setFocusTabIndex(index);

    buttonEl.focus();
  };

  const onKeyDownEvent = (event: React.KeyboardEvent) => {
    event.stopPropagation();

    if (event.code === "ArrowRight" || event.code === "ArrowDown") {
      const nextIndex = focusTabIndex + 1,
        actualNextIndex = getNextTabbableItem(nextIndex);

      focusTabButtonByIndex(actualNextIndex);

      event.preventDefault();
    }
    if (event.code === "ArrowLeft" || event.code === "ArrowUp") {
      const prevIndex = focusTabIndex - 1,
        actualPrevIndex = getPrevTabbableItem(prevIndex);

      focusTabButtonByIndex(actualPrevIndex);

      event.preventDefault();
    }
    if (event.code === "Home") {
      focusTabButtonByIndex(0);

      event.preventDefault();
    }
    if (event.code === "End") {
      focusTabButtonByIndex(section.tabs.length - 1);

      event.preventDefault();
    }
  };

  const getNextTabbableItem = (nextIndex: number): number => {
    if (nextIndex > section.tabs.length - 1) {
      return 0;
    }

    return nextIndex;
  };

  const getPrevTabbableItem = (nextIndex: number): number => {
    if (nextIndex < 0) {
      return section.tabs.length - 1;
    }

    return nextIndex;
  };

  return (
    <BasePageSection section={section}>
      <div className={tabsCss()}>
        <div
          role="tablist"
          aria-label={`${section.title} tabs`}
          className={tablistCss()}
          onKeyDown={onKeyDownEvent}
          ref={tabsRef}
        >
          {section.tabs.map((tab, index) => {
            return (
              <PrimaryButton
                key={tab._key}
                type="button"
                role="tab"
                aria-selected={activeTabIndex === index}
                aria-controls={`tab-content-${tab._key}`}
                id={`tab-${tab._key}`}
                data-index={index}
                onClick={tabClick}
                compact
                appearance={activeTabIndex === index ? "default" : "lightest"}
                className={tabButtonCss()}
                tabIndex={activeTabIndex === index ? 0 : -1}
              >
                {tab.title}
              </PrimaryButton>
            );
          })}
        </div>
      </div>

      {section.tabs.map((tab, index) => {
        const items = tab.items.map(item => {
          item.nested = true;
          switch (item._type) {
            case "gridSection":
              return <GridSection key={section._key} section={item} />;
            case "slidesSection":
              return <SlidesSection key={section._key} section={item} />;
            default:
              return null;
          }
        });

        return (
          <div
            key={tab._key}
            tabIndex={0}
            role="tabpanel"
            id={`tab-content-${tab._key}`}
            aria-labelledby={`tab-${tab._key}`}
            className={tabPanelCss({
              hidden: index === activeTabIndex ? "no" : "yes",
            })}
          >
            {items}
          </div>
        );
      })}
    </BasePageSection>
  );
};

export default TabsSection;
